/**
 * LectureSearch — find the minute, not just the lecture.
 *
 * A student remembers a phrase, not a date. This searches the transcripts of
 * every lesson they are entitled to replay and goes straight to the moment it
 * was said.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Loader2, Bookmark } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (s) => {
  const total = Math.floor(s || 0);
  const m = Math.floor(total / 60);
  return `${m}:${String(total % 60).padStart(2, '0')}`;
};

export const LectureSearch = ({ tenantId }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [phrase, setPhrase] = useState('');
  const [hits, setHits] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');

  const saveClip = async (hit, index) => {
    setSaving(index);
    try {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/study-clips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          recording_id: hit.recording_id,
          start_seconds: Math.max(0, Math.floor(hit.at_seconds) - 5),
          end_seconds: Math.floor(hit.at_seconds) + 55,
          said: hit.said,
          label: '',
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Could not keep that clip');
      toast.success(body.already_saved
        ? 'Already in your saved clips'
        : 'Kept — it is in your saved clips');
      qc.invalidateQueries({ queryKey: ['study-clips', tenantId] });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(null);
    }
  };

  const run = async () => {
    if (phrase.trim().length < 3) { setError('Type at least three characters.'); return; }
    setBusy(true); setError('');
    try {
      const res = await fetch(
        `${API_URL}/api/school/tenants/${tenantId}/recordings/search?q=${encodeURIComponent(phrase.trim())}`,
        { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Search failed');
      setHits(body.hits || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4 mb-4" data-testid="lecture-search">
      <div className="flex flex-wrap items-center gap-2">
        <Search className="h-4 w-4 text-indigo-600" />
        <p className="text-sm font-medium mr-auto">Search what was said in a lesson</p>
      </div>
      <div className="flex gap-2 mt-2">
        <Input value={phrase} onChange={(e) => setPhrase(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
               placeholder="justification by faith"
               data-testid="lecture-search-input" />
        <Button onClick={run} disabled={busy} data-testid="lecture-search-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
        </Button>
      </div>
      {error && <p className="text-xs text-rose-600 mt-2">{error}</p>}
      {hits !== null && hits.length === 0 && !error && (
        <p className="text-xs text-slate-500 mt-3" data-testid="lecture-search-empty">
          Nothing found in the lessons you can watch back. Only lessons that have
          been transcribed can be searched.
        </p>
      )}
      {hits !== null && hits.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="lecture-search-results">
          {hits.map((h, i) => (
            <li key={i} className="flex items-stretch gap-2">
              <button type="button"
                      onClick={() => navigate(
                        `/school/${h.tenant_id || tenantId}/live-classes/${h.session_id}`
                        + `?rec=${h.recording_id}&t=${Math.floor(h.at_seconds)}`)}
                      className="flex-1 text-left border rounded-lg px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800"
                      data-testid={`lecture-hit-${i}`}>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  {h.lesson} · {fmt(h.at_seconds)}
                </p>
                <p className="text-sm">“{h.said}”</p>
              </button>
              <Button variant="outline" size="sm" className="shrink-0 self-center"
                      onClick={() => saveClip(h, i)} disabled={saving === i}
                      data-testid={`save-clip-${i}`}>
                <Bookmark className="h-3.5 w-3.5 mr-1" /> Keep
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export default LectureSearch;
